import { Divider, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { APP_ROUTES } from 'constants/routes';
import WizardLinkButton from 'pages/GettingStarted/Wizard/components/WizardLinkButton';
import WizardPrimaryButton from 'pages/GettingStarted/Wizard/components/WizardPrimaryButton';
import WizardTimeline from 'pages/GettingStarted/Wizard/Phases/WizardTimeline';
import { useWizardUpdate } from 'pages/GettingStarted/Wizard/utils/hook';
import React, { FC } from 'react';
import { useNavigateNoUpdates } from 'routes/RouterUtils';
import {
	defaultWizardState,
	IWizard,
	IWizardProps,
	WizardMode
} from 'types/wizard';

const HomeHeader: FC<IWizardProps> = ({ state }) => {
	const { mode, phase, id } = state;

	const { update, startOver } = useWizardUpdate(state);
	const navigate = useNavigateNoUpdates();
	const currentMode = mode || WizardMode.NotStarted;

	const handleUpdate = React.useCallback(
		(updatedState: Partial<IWizard>, nav: boolean) => async () => {
			await update(updatedState);
			if (nav) {
				navigate(APP_ROUTES.gettingStarted);
			}
		},
		[navigate, update]
	);

	const handleStartOver = React.useCallback(async () => {
		const success = await startOver(id);
		if (success) {
			navigate(APP_ROUTES.gettingStarted);
		}
	}, [id, navigate, startOver]);

	return (
		<Box data-testid="home-header" flexDirection="column" pb={8}>
			<Typography variant="h5" fontWeight="bold">
				{intl.tr('home.header.title')}
			</Typography>
			<Box pt={2}>
				<Typography variant="h6">
					{intl.tr(`home.header.${currentMode}.prefix`)}
				</Typography>
				<Box display="flex" flexDirection="row">
					{currentMode === WizardMode.NotStarted && (
						<Typography
							variant="h6"
							sx={{
								paddingRight: 1
							}}
						>
							{intl.tr(`home.header.${currentMode}.subtitle`)}
						</Typography>
					)}
					<Box display="flex" flexDirection="row">
						<Typography variant="h6" fontWeight="bold">
							{intl.tr(`home.header.${currentMode}.subtitle.bold`)}
						</Typography>
					</Box>
				</Box>
			</Box>
			{currentMode === WizardMode.inProgress && (
				<Box pt={6} maxWidth={600}>
					<WizardTimeline phase={phase} />
				</Box>
			)}
			<Box pt={6} maxWidth={600} display="flex">
				<WizardPrimaryButton
					disabled={false}
					onClick={handleUpdate(
						currentMode === WizardMode.inProgress ? state : defaultWizardState,
						true
					)}
					text={intl.tr(`home.actions.button.${currentMode}`)}
				/>
				<Box pl={3} />
				{currentMode === WizardMode.inProgress && (
					<>
						<WizardLinkButton
							onClick={handleStartOver}
							text={intl.tr('home.actions.button.startOver')}
						/>
						<Box p={1}>
							<Divider orientation="vertical" />
						</Box>
					</>
				)}
				<WizardLinkButton
					onClick={handleUpdate(
						{
							mode: WizardMode.dismissed
						},
						false
					)}
					text={intl.tr('button.dismiss')}
				/>
			</Box>
		</Box>
	);
};

export default HomeHeader;
